import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiArrowRight, FiClock } from 'react-icons/fi';
import { searchApps } from './searchProvider';
import { getFrequentApps, recordAppUsage } from './frequentApps';

/**
 * GlobalSearchModal — command palette mở từ thanh search trên header.
 * - Ô input search ở trên.
 * - Grid "App hay sử dụng" (lưu localStorage qua frequentApps).
 * - Khi search có kết quả thì ẩn grid app hay dùng, hiện danh sách kết quả.
 * Size: max-w-5xl, cao 50vh.
 */
const GlobalSearchModal = ({ show, onHide }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');

  // Reset + focus + khoá scroll nền khi mở
  useEffect(() => {
    if (!show) return undefined;
    setQuery('');
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = '';
    };
  }, [show]);

  // Esc để đóng
  useEffect(() => {
    if (!show) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onHide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onHide]);

  const frequent = useMemo(() => (show ? getFrequentApps(10) : []), [show]);
  const results = useMemo(() => searchApps(query), [query]);
  const hasQuery = query.trim().length > 0;
  const hasResults = hasQuery && results.length > 0;

  if (!show) return null;

  const handleSelect = to => {
    recordAppUsage(to);
    onHide();
    navigate(to);
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter' && hasResults) {
      e.preventDefault();
      handleSelect(results[0].to);
    }
  };

  const modal = (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-start tw-justify-center tw-p-3 sm:tw-p-4 tw-pt-[5vh] sm:tw-pt-[10vh] tw-bg-black tw-bg-opacity-50"
      style={{ zIndex: 99999 }}
      onClick={e => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div
        // `dvh`, and taller on a phone: at 50vh the palette ended up almost
        // entirely behind the on-screen keyboard as soon as the user typed.
        className="tw-bg-white tw-shadow-2xl tw-w-full tw-max-w-5xl tw-rounded-2xl tw-flex tw-flex-col tw-overflow-hidden tw-h-[70dvh] sm:tw-h-[50dvh]"
        style={{ zIndex: 100000 }}
      >
        {/* Search input */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-h-14 tw-border-b tw-border-slate-200 tw-shrink-0">
          <FiSearch className="tw-text-slate-400 tw-flex-shrink-0" size={18} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Searching..."
            aria-label="Search"
            className={
              'tw-flex-1 tw-min-w-0 tw-appearance-none tw-bg-transparent tw-border-0 ' +
              'tw-p-0 tw-m-0 tw-outline-none tw-shadow-none ' +
              'tw-text-[15px] tw-text-slate-900 placeholder:tw-text-slate-400 ' +
              'focus:tw-outline-none focus:tw-ring-0'
            }
          />
          <button
            type="button"
            onClick={onHide}
            aria-label="Close"
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-p-0 tw-bg-transparent tw-border-0 tw-text-slate-400 hover:tw-text-slate-700 hover:tw-bg-slate-100 tw-rounded-md tw-cursor-pointer tw-transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">
          {hasResults ? (
            // Kết quả search
            <ul className="tw-m-0 tw-p-0 tw-list-none tw-flex tw-flex-col tw-gap-1">
              {results.map(app => (
                <li key={app.to}>
                  <button
                    type="button"
                    onClick={() => handleSelect(app.to)}
                    className="tw-group tw-w-full tw-flex tw-items-center tw-gap-3 tw-text-left tw-px-3 tw-py-2.5 tw-bg-transparent tw-border-0 tw-rounded tw-cursor-pointer hover:tw-bg-slate-100 tw-transition-colors"
                  >
                    <FiSearch className="tw-text-slate-300 tw-flex-shrink-0" size={15} />
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-truncate tw-text-[14px] tw-font-medium tw-text-slate-800">
                        {app.label}
                      </span>
                      <span className="tw-block tw-truncate tw-text-[12px] tw-text-slate-400">
                        {app.to}
                      </span>
                    </span>
                    <FiArrowRight className="tw-text-slate-300 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex-shrink-0" size={15} />
                  </button>
                </li>
              ))}
            </ul>
          ) : hasQuery ? (
            // Có gõ nhưng không có kết quả
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-slate-400">
              <FiSearch size={28} className="tw-mb-2" />
              <p className="tw-m-0 tw-text-sm">
                Không tìm thấy kết quả cho &quot;{query}&quot;
              </p>
            </div>
          ) : (
            // Mặc định: app hay sử dụng
            <>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-text-slate-400">
                <FiClock size={13} />
                <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide">
                  Frequently used apps
                </span>
              </div>
              <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-2">
                {frequent.map(app => (
                  <button
                    key={app.to}
                    type="button"
                    onClick={() => handleSelect(app.to)}
                    className="tw-flex tw-flex-col tw-items-start tw-text-left tw-gap-1 tw-px-3 tw-py-3 tw-bg-slate-50 tw-border tw-border-slate-100 tw-rounded-lg tw-cursor-pointer hover:tw-border-primary-300 hover:tw-bg-white hover:tw-shadow-sm tw-transition-all"
                  >
                    <span className="tw-w-full tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-800">
                      {app.label}
                    </span>
                    <span className="tw-w-full tw-truncate tw-text-[11px] tw-text-slate-400">
                      {app.to}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

GlobalSearchModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired
};

export default GlobalSearchModal;
